/**
 * searchShared.tsx — Building blocks compartidos de la página Búsqueda.
 *
 * Módulo permanente del que depende la página real `pages/Search.tsx`
 * (escritorio de widgets configurable, T-018 / DEC-015):
 *
 *   - `useSearchAndInbox()` hook que devuelve TODO el estado + handlers
 *     que la página necesita (form, polling job activo, bandeja, filtros,
 *     columnas, exports).
 *   - Componentes presentacionales puros (reciben props del hook):
 *     `InboxTable`, `ColumnPicker`, `FiltersPanel`, `FiltersInlineChips`,
 *     `EstimateCard`, `ActiveJobBanner`, `JobsPastList`, `NewSearchForm`,
 *     `ExportButtons`, `Paginator`.
 *   - Catálogo `COLUMN_CATALOG` y helpers (`shortUrl`, `statusLabel`,
 *     `hasActiveFilters`, `jobLabel`, `formatDateShort`,
 *     `triggerDownload`, `inboxToCsv`).
 *
 * Las preferencias de columnas se persisten en `mapeodata-inbox-cols-v1`.
 */

import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search as SearchIcon,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Filter,
  X,
  MapPin,
  Star,
  RefreshCw,
  Clock,
  XCircle,
  Download,
  Columns,
  ArrowRight,
} from 'lucide-react'
import { AdminZonePicker } from '@/components/AdminZonePicker'
import { CategoryAutocomplete } from '@/components/CategoryAutocomplete'
import { ConfirmThresholdModal } from '@/components/ConfirmThresholdModal'
import { useMapsIntelligentStore } from '@/store/maps_intelligent_store'
import {
  estimateSearch,
  executeSearch,
  getJob,
  getInbox,
  getInboxMap,
  listMyJobs,
  ListingsError,
  type EstimateResponse,
  type SearchQuery,
  type SearchJob,
  type InboxItem,
  type InboxFilters,
  type InboxOrderBy,
  type InboxOrderDir,
  type UserJobSummary,
} from '@/services/listingsService'
import { getAccessToken } from '@/store/session'
import { DEMO_INBOX, DEMO_JOBS, getDemoInbox } from '@/pages/dashboardDemo'
import { filterLeadsByQuery } from '@/lib/leadSearchFilter'
import { categoryLabel } from '@/lib/categoryLabel'
import {
  opportunityScore,
  leadSignals,
  type OppBand,
} from '@/lib/opportunityScore'

const POLL_INTERVAL_MS = 3000
const INBOX_PAGE_SIZE = 22

/** Preview de desarrollo (bucle de fidelidad): puebla la bandeja con datos demo
 *  cuando no hay backend/sesión. Guardado por DEV + `?preview`; sin efecto en
 *  producción. */
const IS_PREVIEW =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')

/**
 * Carga el dataset COMPLETO del mapa (`GET /listings/inbox/map`, todos los leads
 * del usuario con los filtros activos) — el mapa muestra TODO, no solo la página
 * de la tabla. Se activa con `enabled` (p.ej. pestaña Mapa visible u overlay
 * abierto) y refresca al cambiar los filtros. Mientras no haya cargado, devuelve
 * `fallback` (los items de la página) para no parpadear en vacío; bajo `?preview`
 * sin backend cae a `DEMO_INBOX` para el bucle de fidelidad.
 */
export function useInboxMapItems(
  filters: InboxFilters,
  enabled: boolean,
  fallback: InboxItem[],
): InboxItem[] {
  const [state, setState] = useState<{ loaded: boolean; items: InboxItem[] }>({
    loaded: false,
    items: [],
  })
  const filtersKey = JSON.stringify(filters)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const ctrl = new AbortController()
    getInboxMap(filters, ctrl.signal)
      .then((items) => { if (!cancelled) setState({ loaded: true, items }) })
      .catch((e) => {
        if (cancelled || (e as { code?: string })?.code === 'aborted') return
        // Mapa en preview: respeta ?mapcase (big/onecity) para el bucle de fidelidad.
        if (IS_PREVIEW) setState({ loaded: true, items: getDemoInbox() })
        // sin preview: dejamos loaded=false → se usa el fallback (página) sin romper.
      })
    return () => { cancelled = true; ctrl.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filtersKey])

  return state.loaded ? state.items : fallback
}

/**
 * Carga el HISTORIAL COMPLETO de búsquedas del usuario (`GET /listings/jobs`).
 * El endpoint pagina con `offset`/`limit` (cap 200 por página, verificado en el
 * backend) y devuelve `total`, así que recorremos las páginas hasta completar
 * el histórico — no hay límite duro de jobs, solo de tamaño de página. Se activa
 * con `enabled` (pestaña Historial visible) y carga UNA vez. Bajo `?preview` sin
 * backend cae a `DEMO_JOBS` para el bucle de fidelidad.
 *
 * `capped` queda en `true` solo si el usuario supera el tope de seguridad
 * (`MAX_HISTORY` jobs) — caso extremo que avisamos en la UI sin asumir nada.
 */
const JOBS_PAGE_SIZE = 200
const MAX_HISTORY = 2000

export interface AllJobsState {
  jobs: UserJobSummary[]
  total: number
  loading: boolean
  loaded: boolean
  capped: boolean
  error: boolean
}

export function useAllJobs(enabled: boolean): AllJobsState {
  const [state, setState] = useState<AllJobsState>({
    jobs: [],
    total: 0,
    loading: false,
    loaded: false,
    capped: false,
    error: false,
  })

  useEffect(() => {
    if (!enabled || state.loaded) return
    let cancelled = false
    const ctrl = new AbortController()
    ;(async () => {
      setState(s => ({ ...s, loading: true, error: false }))
      try {
        let offset = 0
        let total = 0
        const all: UserJobSummary[] = []
        for (;;) {
          const resp = await listMyJobs({
            offset,
            limit: JOBS_PAGE_SIZE,
            signal: ctrl.signal,
          })
          total = resp.total
          all.push(...resp.items)
          offset += resp.items.length
          if (
            resp.items.length < JOBS_PAGE_SIZE ||
            all.length >= total ||
            all.length >= MAX_HISTORY
          )
            break
        }
        if (!cancelled)
          setState({
            jobs: all,
            total,
            loading: false,
            loaded: true,
            capped: all.length < total,
            error: false,
          })
      } catch (e) {
        if (cancelled || (e as { code?: string })?.code === 'aborted') return
        if (IS_PREVIEW) {
          setState({
            jobs: DEMO_JOBS,
            total: DEMO_JOBS.length,
            loading: false,
            loaded: true,
            capped: false,
            error: false,
          })
          return
        }
        setState(s => ({ ...s, loading: false, loaded: true, error: true }))
      }
    })()
    return () => {
      cancelled = true
      ctrl.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, state.loaded])

  return state
}

// ============================================================================
// Catálogo de columnas de la bandeja: 24 columnas con su render + persistencia.
// ============================================================================

export type ColumnId =
  | 'name'
  | 'category'
  | 'additional_cats'
  | 'description'
  | 'city'
  | 'region'
  | 'country'
  | 'zip'
  | 'address'
  | 'phone'
  | 'email'
  | 'url'
  | 'domain'
  | 'rating'
  | 'votes'
  | 'claimed'
  | 'status'
  | 'price'
  | 'photos'
  | 'topics'
  | 'maps'
  | 'jobs_count'
  | 'extracted'
  | 'place_id'

interface ColumnDef {
  id: ColumnId
  label: string
  defaultVisible: boolean
  render: (item: InboxItem) => ReactNode
  /** Si la columna es ordenable server-side, su clave en el backend (T-029). */
  sortKey?: InboxOrderBy
}

/** Dirección de orden por defecto al activar una columna: textos asc, el resto desc. */
const SORT_TEXT_COLS = new Set<InboxOrderBy>(['title', 'category_primary', 'city'])
export function defaultSortDir(by: InboxOrderBy): InboxOrderDir {
  return SORT_TEXT_COLS.has(by) ? 'asc' : 'desc'
}

export const COLUMN_CATALOG: ColumnDef[] = [
  {
    id: 'name',
    sortKey: 'title',
    label: 'Nombre',
    defaultVisible: true,
    render: i => <strong className="text-gray-900">{i.title || '—'}</strong>,
  },
  {
    id: 'category',
    sortKey: 'category_primary',
    label: 'Categoría',
    defaultVisible: true,
    render: i => (i.category_primary ? categoryLabel(i.category_primary) : '—'),
  },
  {
    id: 'additional_cats',
    label: 'Categorías 2as',
    defaultVisible: false,
    render: i =>
      i.additional_categories && i.additional_categories.length > 0
        ? i.additional_categories.slice(0, 3).join(', ') +
          (i.additional_categories.length > 3 ? '…' : '')
        : '—',
  },
  {
    id: 'description',
    label: 'Descripción',
    defaultVisible: false,
    render: i =>
      i.description ? (
        <span className="line-clamp-2 max-w-[280px] text-[11px] text-gray-600">
          {i.description}
        </span>
      ) : (
        '—'
      ),
  },
  {
    id: 'city',
    sortKey: 'city',
    label: 'Ciudad',
    defaultVisible: true,
    render: i => i.address_components?.city || '—',
  },
  {
    id: 'region',
    label: 'Región',
    defaultVisible: false,
    render: i => i.address_components?.region || '—',
  },
  {
    id: 'country',
    label: 'País',
    defaultVisible: false,
    render: i => i.address_components?.country_code?.toUpperCase() || '—',
  },
  {
    id: 'zip',
    label: 'C.P.',
    defaultVisible: true,
    render: i => i.address_components?.zip || '—',
  },
  {
    id: 'address',
    label: 'Dirección',
    defaultVisible: false,
    render: i => (
      <span className="line-clamp-1 max-w-[260px] text-xs text-gray-600">
        {i.address_full || '—'}
      </span>
    ),
  },
  {
    id: 'phone',
    label: 'Teléfono',
    defaultVisible: true,
    render: i =>
      i.phone ? (
        <a
          href={`tel:${i.phone.replace(/\s/g, '')}`}
          className="text-blue-600 hover:text-blue-800"
        >
          {i.phone}
        </a>
      ) : (
        '—'
      ),
  },
  {
    id: 'email',
    label: 'Email',
    defaultVisible: true,
    render: i =>
      i.email_primary ? (
        <a
          href={`mailto:${i.email_primary}`}
          className="text-blue-600 hover:text-blue-800"
        >
          {i.email_primary}
        </a>
      ) : (
        '—'
      ),
  },
  {
    id: 'url',
    label: 'Web',
    defaultVisible: true,
    render: i =>
      i.url ? (
        <a
          href={i.url}
          target="_blank"
          rel="noopener noreferrer"
          className="line-clamp-1 max-w-[200px] text-blue-600 hover:text-blue-800"
          title={i.url}
        >
          {shortUrl(i.url)}
        </a>
      ) : (
        '—'
      ),
  },
  {
    id: 'domain',
    label: 'Dominio',
    defaultVisible: false,
    render: i => i.domain || '—',
  },
  {
    id: 'rating',
    sortKey: 'rating_value',
    label: 'Valoración',
    defaultVisible: true,
    render: i =>
      typeof i.rating_value === 'number' ? (
        <span className="inline-flex items-center gap-1">
          <Star size={12} className="fill-amber-500 text-amber-500" />
          <strong>{i.rating_value.toFixed(1)}</strong>
        </span>
      ) : (
        '—'
      ),
  },
  {
    id: 'votes',
    sortKey: 'rating_votes_count',
    label: 'Reseñas',
    defaultVisible: true,
    render: i =>
      typeof i.rating_votes_count === 'number'
        ? i.rating_votes_count.toLocaleString('es-ES')
        : '—',
  },
  {
    id: 'claimed',
    sortKey: 'is_claimed',
    label: 'Reclamado',
    defaultVisible: false,
    render: i =>
      i.is_claimed === true ? (
        <span className="text-emerald-700">Sí</span>
      ) : i.is_claimed === false ? (
        <span className="text-amber-700">No</span>
      ) : (
        '—'
      ),
  },
  {
    id: 'status',
    label: 'Estado',
    defaultVisible: false,
    render: i => statusLabel(i.current_status),
  },
  {
    id: 'price',
    label: 'Precio',
    defaultVisible: false,
    render: i => i.price_level || '—',
  },
  {
    id: 'photos',
    label: 'Fotos',
    defaultVisible: false,
    render: i =>
      typeof i.total_photos === 'number'
        ? i.total_photos.toLocaleString('es-ES')
        : '—',
  },
  {
    id: 'topics',
    label: 'Topics reseñas',
    defaultVisible: false,
    render: i => {
      const t = i.place_topics
      if (!t || typeof t !== 'object') return '—'
      const keys = Object.keys(t as Record<string, unknown>)
      if (keys.length === 0) return '—'
      return (
        <span className="text-[11px] text-gray-600">
          {keys.slice(0, 3).join(', ')}
          {keys.length > 3 ? '…' : ''}
        </span>
      )
    },
  },
  {
    id: 'maps',
    label: 'Maps',
    defaultVisible: true,
    render: i =>
      i.check_url ? (
        <a
          href={i.check_url}
          target="_blank"
          rel="noopener noreferrer"
          title="Ver en Google Maps"
          className="text-gray-500 hover:text-gray-700"
        >
          <MapPin size={14} />
        </a>
      ) : (
        '—'
      ),
  },
  {
    id: 'jobs_count',
    sortKey: 'jobs_count',
    label: 'Búsquedas',
    defaultVisible: false,
    render: i =>
      i.jobs_count > 1 ? (
        <span className="inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
          {i.jobs_count}
        </span>
      ) : (
        '1'
      ),
  },
  {
    id: 'extracted',
    sortKey: 'first_extracted_at',
    label: 'Extraído',
    defaultVisible: true,
    render: i => formatDateShort(i.first_extracted_at),
  },
  {
    id: 'place_id',
    label: 'Place ID',
    defaultVisible: false,
    render: i => (
      <span className="font-mono text-[10px] text-gray-500">{i.place_id}</span>
    ),
  },
]

const DEFAULT_VISIBLE_COLS: ColumnId[] = COLUMN_CATALOG.filter(
  c => c.defaultVisible,
).map(c => c.id)

const COLUMN_STORAGE_KEY = 'mapeodata-inbox-cols-v1'

/** Orden de la tabla de leads (server-side), persistido (T-029). `by:null` = orden
 *  por defecto del backend (`first_extracted_at DESC`). */
export interface InboxSort {
  by: InboxOrderBy | null
  dir: InboxOrderDir
}
const INBOX_SORT_STORAGE_KEY = 'mapeodata-inbox-sort-v1'
const INBOX_SORT_COLS: InboxOrderBy[] = [
  'title', 'category_primary', 'city', 'rating_value',
  'rating_votes_count', 'is_claimed', 'first_extracted_at', 'jobs_count',
]
function loadInboxSort(): InboxSort {
  // Deep-link `?sort=<col>` o `?sort=<col>:<dir>` (útil para captura de fidelidad).
  try {
    if (typeof window !== 'undefined') {
      const param = new URLSearchParams(window.location.search).get('sort')
      if (param) {
        const [byRaw, dirRaw] = param.split(':')
        if ((INBOX_SORT_COLS as string[]).includes(byRaw)) {
          return { by: byRaw as InboxOrderBy, dir: dirRaw === 'asc' ? 'asc' : 'desc' }
        }
      }
    }
  } catch {
    // ignore
  }
  try {
    const raw = localStorage.getItem(INBOX_SORT_STORAGE_KEY)
    if (raw) {
      const p = JSON.parse(raw) as Partial<InboxSort>
      if (p && p.by && INBOX_SORT_COLS.includes(p.by)) {
        return { by: p.by, dir: p.dir === 'asc' ? 'asc' : 'desc' }
      }
    }
  } catch {
    // ignore
  }
  return { by: null, dir: 'desc' }
}

/** Comparador client-side por columna whitelisted (solo para el fallback DEMO de
 *  preview; en producción ordena el backend). */
function sortDemoInbox(items: InboxItem[], sort: InboxSort): InboxItem[] {
  if (!sort.by) return items
  const by = sort.by
  const val = (i: InboxItem): string | number => {
    switch (by) {
      case 'title': return (i.title ?? '').toLowerCase()
      case 'category_primary': return (i.category_primary ?? '').toLowerCase()
      case 'city': return (i.address_components?.city ?? '').toLowerCase()
      case 'rating_value': return i.rating_value ?? -1
      case 'rating_votes_count': return i.rating_votes_count ?? -1
      case 'is_claimed': return i.is_claimed === true ? 1 : i.is_claimed === false ? 0 : -1
      case 'first_extracted_at': return i.first_extracted_at ?? ''
      case 'jobs_count': return i.jobs_count ?? 0
      default: return ''
    }
  }
  const dir = sort.dir === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    const va = val(a), vb = val(b)
    if (va < vb) return -1 * dir
    if (va > vb) return 1 * dir
    return 0
  })
}
function saveInboxSort(s: InboxSort) {
  try {
    localStorage.setItem(INBOX_SORT_STORAGE_KEY, JSON.stringify(s))
  } catch {
    // ignore
  }
}

function loadVisibleColsFromStorage(): Set<ColumnId> {
  try {
    const raw = localStorage.getItem(COLUMN_STORAGE_KEY)
    if (raw) {
      const arr = JSON.parse(raw) as unknown
      if (Array.isArray(arr)) {
        const valid = (arr as string[]).filter(id =>
          COLUMN_CATALOG.some(c => c.id === id),
        ) as ColumnId[]
        return new Set(valid)
      }
    }
  } catch {
    // ignore
  }
  return new Set(DEFAULT_VISIBLE_COLS)
}

function saveVisibleColsToStorage(set: Set<ColumnId>) {
  try {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(Array.from(set)))
  } catch {
    // ignore
  }
}

// ============================================================================
// Hook único: estado y handlers que las 4 disposiciones necesitan.
// ============================================================================

export interface UseSearchAndInbox {
  // — Formulario nueva búsqueda
  selectedCategoryIds: string[]
  setSelectedCategoryIds: (ids: string[]) => void
  estimate: EstimateResponse | null
  estimateError: string | null
  estimateLoading: boolean
  searchError: string | null
  searchLoading: boolean
  canEstimate: boolean
  canExtract: boolean
  handleEstimate: () => Promise<void>
  handleExtract: () => void
  // — Modal confirm
  confirmModalOpen: boolean
  closeConfirmModal: () => void
  confirmExtract: () => Promise<void>
  // — Job activo (polling)
  activeJob: SearchJob | null
  // — Bandeja
  filters: InboxFilters
  setFilters: (f: InboxFilters) => void
  items: InboxItem[]
  totalItems: number
  offset: number
  loadingInbox: boolean
  inboxError: string | null
  jobs: UserJobSummary[]
  applyFilters: (f: InboxFilters) => void
  clearFilters: () => void
  pageSize: number
  loadInbox: (newOffset: number, useFilters?: InboxFilters) => void
  // — Orden de la tabla (server-side, T-029)
  inboxSort: InboxSort
  setInboxColumnSort: (by: InboxOrderBy) => void
  // — Columnas
  visibleCols: Set<ColumnId>
  toggleCol: (id: ColumnId) => void
  resetCols: () => void
  showAllCols: () => void
  // — Exports
  exportLoading: null | 'csv' | 'xlsx' | 'json'
  handleExportInbox: (format: 'csv' | 'xlsx' | 'json') => Promise<void>
}

export function useSearchAndInbox(): UseSearchAndInbox {
  const navigate = useNavigate()

  // Formulario
  const zone_mode = useMapsIntelligentStore(s => s.zone_mode)
  const zone_admin = useMapsIntelligentStore(s => s.zone_admin)
  const zone_circle = useMapsIntelligentStore(s => s.zone_circle)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([])
  const [estimate, setEstimate] = useState<EstimateResponse | null>(null)
  const [estimateError, setEstimateError] = useState<string | null>(null)
  const [estimateLoading, setEstimateLoading] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)

  const has_zone =
    zone_mode === 'admin'
      ? zone_admin.selected_zone !== null
      : Number.isFinite(zone_circle.center_lat) &&
        Number.isFinite(zone_circle.center_lng) &&
        zone_circle.radius_km > 0
  const has_categories = selectedCategoryIds.length > 0
  const canEstimate = has_categories && has_zone && !estimateLoading
  const canExtract =
    estimate !== null && estimate.fits_in_quota && !estimate.exceeds_max && !searchLoading

  const buildSearchQuery = useCallback((): SearchQuery => {
    const query: SearchQuery = { categories: selectedCategoryIds }
    if (zone_mode === 'admin' && zone_admin.selected_zone) {
      query.zone_id = zone_admin.selected_zone.id
    } else if (zone_mode === 'free_circle') {
      query.zone_query = {
        kind: 'free_circle',
        center_lat: zone_circle.center_lat,
        center_lng: zone_circle.center_lng,
        radius_km: zone_circle.radius_km,
      }
    }
    return query
  }, [selectedCategoryIds, zone_mode, zone_admin, zone_circle])

  const handleEstimate = useCallback(async () => {
    setEstimateError(null)
    setEstimate(null)
    setSearchError(null)
    setEstimateLoading(true)
    try {
      const result = await estimateSearch(buildSearchQuery())
      setEstimate(result)
    } catch (e) {
      if (e instanceof ListingsError && e.code === 'auth') {
        navigate('/login')
        return
      }
      setEstimateError(
        e instanceof ListingsError
          ? e.code === 'validation'
            ? `Datos inválidos${e.details ? ': ' + e.details.join('; ') : ''}`
            : e.message
          : e instanceof Error
            ? e.message
            : 'No se pudo estimar el coste de la búsqueda.',
      )
    } finally {
      setEstimateLoading(false)
    }
  }, [buildSearchQuery, navigate])

  // Bandeja (declarado antes porque doExecuteSearch necesita loadInbox).
  const [filters, setFilters] = useState<InboxFilters>({})
  const [items, setItems] = useState<InboxItem[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loadingInbox, setLoadingInbox] = useState(true)
  const [inboxError, setInboxError] = useState<string | null>(null)
  const [jobs, setJobs] = useState<UserJobSummary[]>([])
  const [exportLoading, setExportLoading] = useState<null | 'csv' | 'xlsx' | 'json'>(null)
  const [visibleCols, setVisibleCols] = useState<Set<ColumnId>>(
    loadVisibleColsFromStorage,
  )

  // Orden server-side de la tabla (T-029). Ref para leer el valor vigente dentro
  // de loadInbox sin recrear el callback en cada cambio de orden.
  const [inboxSort, setInboxSortState] = useState<InboxSort>(loadInboxSort)
  const inboxSortRef = useRef<InboxSort>(inboxSort)
  inboxSortRef.current = inboxSort

  const loadInbox = useCallback(
    async (newOffset = 0, useFilters: InboxFilters = filters) => {
      setLoadingInbox(true)
      setInboxError(null)
      try {
        const sort = inboxSortRef.current
        const resp = await getInbox(useFilters, {
          offset: newOffset,
          limit: INBOX_PAGE_SIZE,
          order_by: sort.by ?? undefined,
          order_dir: sort.by ? sort.dir : undefined,
        })
        setItems(resp.items)
        setTotalItems(resp.total)
        setOffset(resp.offset)
      } catch (e) {
        if (IS_PREVIEW) {
          // Sin backend, replicamos el motor `q` multicampo + el orden en cliente
          // para que el buscador de la página se vea filtrando en el preview.
          const q = (useFilters.q ?? '').trim()
          const base = q ? filterLeadsByQuery(DEMO_INBOX, q) : DEMO_INBOX
          setItems(sortDemoInbox(base, inboxSortRef.current))
          setTotalItems(base.length)
          setOffset(0)
          return
        }
        if (e instanceof ListingsError && e.code === 'auth') {
          navigate('/login')
          return
        }
        setInboxError(
          e instanceof Error ? e.message : 'No se pudo cargar la bandeja.',
        )
      } finally {
        setLoadingInbox(false)
      }
    },
    [filters, navigate],
  )

  const loadJobs = useCallback(async () => {
    try {
      const resp = await listMyJobs({ limit: 200 })
      setJobs(resp.items)
    } catch {
      if (IS_PREVIEW) setJobs(DEMO_JOBS)
    }
  }, [])

  // Job activo (polling)
  const [activeJob, setActiveJob] = useState<SearchJob | null>(null)
  const [polling, setPolling] = useState(false)

  const doExecuteSearch = useCallback(
    async (confirmed: boolean) => {
      if (!estimate) return
      setSearchError(null)
      setSearchLoading(true)
      try {
        const resp = await executeSearch(
          buildSearchQuery(),
          estimate.leads_to_consume,
          { confirmed },
        )
        setConfirmModalOpen(false)
        setActiveJob(resp.job)
        if (resp.job.status === 'completed' || !resp.async) {
          await loadInbox(0, filters)
          await loadJobs()
          setActiveJob(null)
          setPolling(false)
        } else {
          setPolling(true)
        }
      } catch (e) {
        setConfirmModalOpen(false)
        if (e instanceof ListingsError && e.code === 'auth') {
          navigate('/login')
          return
        }
        setSearchError(
          e instanceof ListingsError
            ? e.message
            : e instanceof Error
              ? e.message
              : 'No se pudo lanzar la extracción.',
        )
      } finally {
        setSearchLoading(false)
      }
    },
    [buildSearchQuery, estimate, filters, loadInbox, loadJobs, navigate],
  )

  const handleExtract = useCallback(() => {
    if (!estimate) return
    if (estimate.requires_confirmation) {
      setConfirmModalOpen(true)
      return
    }
    doExecuteSearch(false)
  }, [estimate, doExecuteSearch])

  useEffect(() => {
    if (!polling || !activeJob) return
    let cancelled = false
    const tick = async () => {
      try {
        const job = await getJob(activeJob.id)
        if (cancelled) return
        setActiveJob(job)
        if (job.status === 'completed') {
          setPolling(false)
          await loadInbox(0, filters)
          await loadJobs()
          setActiveJob(null)
        } else if (job.status === 'failed' || job.status === 'canceled') {
          setPolling(false)
        }
      } catch {
        // siguiente tick reintenta
      }
    }
    const id = window.setInterval(tick, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, activeJob?.id])

  // Carga inicial
  const initialLoadDone = useRef(false)
  useEffect(() => {
    if (initialLoadDone.current) return
    initialLoadDone.current = true
    loadInbox(0, {})
    loadJobs()
  }, [loadInbox, loadJobs])

  const applyFilters = useCallback(
    (next: InboxFilters) => {
      setFilters(next)
      loadInbox(0, next)
    },
    [loadInbox],
  )

  const clearFilters = useCallback(() => {
    setFilters({})
    loadInbox(0, {})
  }, [loadInbox])

  // Ordenar la tabla por una columna whitelisted (T-029). Click en cabecera:
  // misma columna → alterna asc/desc; otra columna → su dirección por defecto.
  // Persiste y recarga el inbox desde el backend (orden sobre TODO el conjunto).
  const setInboxColumnSort = useCallback(
    (by: InboxOrderBy) => {
      const cur = inboxSortRef.current
      const next: InboxSort =
        cur.by === by
          ? { by, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
          : { by, dir: defaultSortDir(by) }
      inboxSortRef.current = next
      setInboxSortState(next)
      saveInboxSort(next)
      loadInbox(0)
    },
    [loadInbox],
  )

  const toggleCol = useCallback((id: ColumnId) => {
    setVisibleCols(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      saveVisibleColsToStorage(next)
      return next
    })
  }, [])

  const resetCols = useCallback(() => {
    const next = new Set(DEFAULT_VISIBLE_COLS)
    setVisibleCols(next)
    saveVisibleColsToStorage(next)
  }, [])

  const showAllCols = useCallback(() => {
    const next = new Set(COLUMN_CATALOG.map(c => c.id))
    setVisibleCols(next)
    saveVisibleColsToStorage(next)
  }, [])

  const handleExportInbox = useCallback(
    async (format: 'csv' | 'xlsx' | 'json') => {
      setExportLoading(format)
      try {
        const base = (import.meta.env.VITE_API_URL as string | undefined) ?? ''
        if (filters.job_id) {
          const url = `${base}/api/v1/listings/jobs/${encodeURIComponent(
            filters.job_id,
          )}/results?format=${format}&limit=10000`
          const token = getAccessToken()
          const resp = await fetch(url, {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
          const blob = await resp.blob()
          triggerDownload(
            blob,
            `mapeodata-job-${filters.job_id.slice(0, 8)}.${format}`,
          )
        } else {
          if (format === 'json') {
            const blob = new Blob([JSON.stringify(items, null, 2)], {
              type: 'application/json',
            })
            triggerDownload(blob, `mapeodata-inbox.json`)
          } else if (format === 'csv') {
            const csv = inboxToCsv(items)
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
            triggerDownload(blob, `mapeodata-inbox.csv`)
          } else {
            setInboxError(
              'Exportar Excel sin filtro de búsqueda concreta llegará en una próxima iteración.',
            )
          }
        }
      } catch (e) {
        setInboxError(e instanceof Error ? e.message : 'No se pudo exportar.')
      } finally {
        setExportLoading(null)
      }
    },
    [filters.job_id, items],
  )

  return {
    selectedCategoryIds,
    setSelectedCategoryIds: ids => {
      setSelectedCategoryIds(ids)
      setEstimate(null)
    },
    estimate,
    estimateError,
    estimateLoading,
    searchError,
    searchLoading,
    canEstimate,
    canExtract,
    handleEstimate,
    handleExtract,
    confirmModalOpen,
    closeConfirmModal: () => setConfirmModalOpen(false),
    confirmExtract: () => doExecuteSearch(true),
    activeJob,
    filters,
    setFilters,
    items,
    totalItems,
    offset,
    loadingInbox,
    inboxError,
    jobs,
    applyFilters,
    clearFilters,
    pageSize: INBOX_PAGE_SIZE,
    loadInbox,
    inboxSort,
    setInboxColumnSort,
    visibleCols,
    toggleCol,
    resetCols,
    showAllCols,
    exportLoading,
    handleExportInbox,
  }
}

// ============================================================================
// Componentes presentacionales reutilizables
// ============================================================================

export function NewSearchForm({
  state,
  compact = false,
  dropdownZIndex,
}: {
  state: UseSearchAndInbox
  compact?: boolean
  /** z-index del desplegable de categorías. En el modal de nueva extracción
   *  (overlay z-80) se sube por encima; en el sidebar se deja el default. */
  dropdownZIndex?: number
}) {
  return (
    <div className={'cabina-form ' + (compact ? 'space-y-3' : 'grid gap-5 lg:grid-cols-3')}>
      <div className={compact ? '' : 'space-y-5 lg:col-span-2'}>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Categorías
          </label>
          <CategoryAutocomplete
            value={state.selectedCategoryIds}
            onChange={state.setSelectedCategoryIds}
            lang="es"
            country="ES"
            placeholder="abogado, dentista, restaurante…"
            maxSelections={10}
            dropdownZIndex={dropdownZIndex}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Zona
          </label>
          <AdminZonePicker />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            type="button"
            disabled={!state.canEstimate}
            onClick={state.handleEstimate}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {state.estimateLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Estimando…
              </>
            ) : (
              <>
                <SearchIcon size={14} /> Estimar
              </>
            )}
          </button>
          <button
            type="button"
            disabled={!state.canExtract}
            onClick={state.handleExtract}
            className={
              'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors ' +
              (state.canExtract
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'cursor-not-allowed border border-gray-300 bg-white text-gray-400')
            }
          >
            {state.searchLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Extrayendo…
              </>
            ) : (
              'Extraer leads'
            )}
          </button>
        </div>

        {state.estimateError && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{state.estimateError}</span>
          </div>
        )}
        {state.searchError && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{state.searchError}</span>
          </div>
        )}
      </div>

      <aside>
        {state.estimate ? (
          <EstimateCard estimate={state.estimate} />
        ) : (
          <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-4 text-xs text-gray-500">
            Selecciona categoría + zona y pulsa <strong>Estimar</strong> para
            ver cuántos leads encontraremos antes de gastar cupo.
          </div>
        )}
      </aside>
    </div>
  )
}

export function EstimateCard({ estimate }: { estimate: EstimateResponse }) {
  const pct_used = Math.min(
    100,
    Math.round(
      ((estimate.monthly_leads_quota -
        Math.max(0, estimate.leads_remaining_after)) /
        Math.max(1, estimate.monthly_leads_quota)) *
        100,
    ),
  )

  const banner_class =
    estimate.quota_code === 'ok'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : estimate.quota_code === 'requires_confirm' ||
          estimate.quota_code === 'requires_2fa'
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-red-200 bg-red-50 text-red-900'

  const Icon =
    estimate.fits_in_quota && !estimate.exceeds_max ? CheckCircle2 : AlertCircle

  return (
    <div className="space-y-3">
      <div
        className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${banner_class}`}
      >
        <Icon size={16} className="mt-0.5 flex-shrink-0" />
        <span>{estimate.quota_message}</span>
      </div>
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <Metric
          label="Leads"
          value={estimate.total_count_estimate.toLocaleString('es-ES')}
        />
        <Metric
          label="Consumirá"
          value={estimate.leads_to_consume.toLocaleString('es-ES')}
        />
        <Metric
          label="Restará"
          value={Math.max(0, estimate.leads_remaining_after).toLocaleString(
            'es-ES',
          )}
        />
        <Metric label="Plan" value={estimate.plan_code} />
      </dl>
      <div>
        <div className="mb-1 flex items-baseline justify-between text-[11px] text-gray-600">
          <span>Cupo mensual</span>
          <span>
            {(
              estimate.monthly_leads_quota - estimate.leads_remaining_after
            ).toLocaleString('es-ES')}{' '}
            / {estimate.monthly_leads_quota.toLocaleString('es-ES')}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full transition-all ${
              pct_used >= 90
                ? 'bg-red-500'
                : pct_used >= 70
                  ? 'bg-amber-500'
                  : 'bg-blue-500'
            }`}
            style={{ width: `${pct_used}%` }}
          />
        </div>
      </div>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-gray-100 bg-gray-50 px-2 py-1.5">
      <dt className="text-[10px] uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm font-semibold text-gray-900">{value}</dd>
    </div>
  )
}

export function ActiveJobBanner({ job }: { job: SearchJob }) {
  const config = {
    pending: {
      Icon: Clock,
      cls: 'border-amber-200 bg-amber-50 text-amber-900',
      label: 'En cola',
      spin: false,
    },
    running: {
      Icon: RefreshCw,
      cls: 'border-blue-200 bg-blue-50 text-blue-900',
      label: 'Extrayendo leads…',
      spin: true,
    },
    completed: {
      Icon: CheckCircle2,
      cls: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      label: 'Completado',
      spin: false,
    },
    failed: {
      Icon: XCircle,
      cls: 'border-red-200 bg-red-50 text-red-900',
      label: 'Falló',
      spin: false,
    },
    cancelled: {
      Icon: XCircle,
      cls: 'border-gray-200 bg-gray-50 text-gray-700',
      label: 'Cancelado',
      spin: false,
    },
    canceled: {
      Icon: XCircle,
      cls: 'border-gray-200 bg-gray-50 text-gray-700',
      label: 'Cancelado',
      spin: false,
    },
  } as const

  const c = config[job.status]
  const Icon = c.Icon
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${c.cls}`}
    >
      <Icon
        size={18}
        className={`mt-0.5 flex-shrink-0 ${c.spin ? 'animate-spin' : ''}`}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{c.label}</p>
        <p className="mt-0.5 text-xs">
          {job.result_listings_count.toLocaleString('es-ES')} leads
          {job.total_count_estimate
            ? ` · estimados ~${job.total_count_estimate.toLocaleString('es-ES')}`
            : ''}
        </p>
      </div>
    </div>
  )
}

export function FiltersPanel({
  filters,
  jobs,
  onApply,
  onClear,
  bare = false,
}: {
  filters: InboxFilters
  jobs: UserJobSummary[]
  onApply: (f: InboxFilters) => void
  onClear: () => void
  /** Cuando `true`, omite el contenedor con borde + el título "Filtros".
   *  Útil al incrustar el panel dentro de un widget que ya aporta su
   *  propia cabecera (T-018 escritorio de widgets). */
  bare?: boolean
}) {
  const [local, setLocal] = useState<InboxFilters>(filters)

  useEffect(() => {
    setLocal(filters)
  }, [filters])

  const set = <K extends keyof InboxFilters>(k: K, v: InboxFilters[K]) =>
    setLocal(prev => ({ ...prev, [k]: v }))

  return (
    <aside className={bare ? 'space-y-3.5' : 'cab-filters space-y-3.5'}>
      {!bare && <h3 className="text-sm font-semibold" style={{ color: 'var(--fg)' }}>Filtros</h3>}

      {/* El texto libre (`q`, multicampo) lo gobierna ahora el BUSCADOR
          prominente de la bandeja; aquí solo quedan los filtros estructurales.
          La búsqueda activa sigue apareciendo como chip eliminable. */}
      <div className="cab-field" style={{ margin: 0 }}>
        <label>Mis búsquedas</label>
        <select
          className="cab-input"
          value={local.job_id ?? ''}
          onChange={e => set('job_id', e.target.value || undefined)}
        >
          <option value="">Todas mis búsquedas</option>
          {jobs.map(j => (
            <option key={j.id} value={j.id}>{jobLabel(j)}</option>
          ))}
        </select>
      </div>

      {/* Contacto disponible */}
      <div>
        <div className="panel-label" style={{ margin: '0 0 7px 0' }}>Contacto disponible</div>
        <div className="space-y-1.5">
          <FilterToggle label="Email" value={local.has_email} onChange={v => set('has_email', v)} />
          <FilterToggle label="Teléfono" value={local.has_phone} onChange={v => set('has_phone', v)} />
          <FilterToggle label="Web" value={local.has_url} onChange={v => set('has_url', v)} />
        </div>
      </div>

      {/* Estado del negocio */}
      <div>
        <div className="panel-label" style={{ margin: '0 0 7px 0' }}>Estado del negocio</div>
        <FilterToggle label="Perfil reclamado" value={local.is_claimed} onChange={v => set('is_claimed', v)} />
      </div>

      {/* Filtro negativo: excluir a quien YA tiene lo que el usuario vende. */}
      <div>
        <div className="panel-label" style={{ margin: '0 0 6px 0' }}>Filtro negativo · excluir</div>
        <label className="cab-check">
          <span className={`box${local.has_url === false ? ' on' : ''}`} />
          <input
            type="checkbox"
            style={{ display: 'none' }}
            checked={local.has_url === false}
            onChange={e => set('has_url', e.target.checked ? false : undefined)}
          />
          Excluir negocios con web
          <span className="sig" style={{ marginLeft: 'auto', background: 'var(--danger-soft)', color: 'var(--danger)' }}>excluir</span>
        </label>
        <label className="cab-check">
          <span className={`box${local.is_claimed === false ? ' on' : ''}`} />
          <input
            type="checkbox"
            style={{ display: 'none' }}
            checked={local.is_claimed === false}
            onChange={e => set('is_claimed', e.target.checked ? false : undefined)}
          />
          Excluir ya reclamados
          <span className="sig" style={{ marginLeft: 'auto', background: 'var(--danger-soft)', color: 'var(--danger)' }}>excluir</span>
        </label>
        <p style={{ fontSize: 10.5, color: 'var(--meta)', margin: '7px 2px 0', lineHeight: 1.45 }}>
          Oculta a quien ya tiene lo que vendes (p.ej. web) para quedarte solo con la oportunidad real.
        </p>
      </div>

      {/* Valoración mínima */}
      <div>
        <div className="panel-label" style={{ margin: '0 0 7px 0' }}>Valoración mínima</div>
        <input
          type="number"
          min={0} max={5} step={0.5}
          className="cab-input"
          value={local.rating_min ?? ''}
          onChange={e => set('rating_min', e.target.value === '' ? undefined : Number(e.target.value))}
          placeholder="ej. 4 (★ mínimas)"
        />
      </div>

      {/* Ubicación */}
      <div>
        <div className="panel-label" style={{ margin: '0 0 7px 0' }}>Ubicación</div>
        <input
          type="text"
          maxLength={2}
          className="cab-input"
          style={{ textTransform: 'uppercase' }}
          value={local.country_code ?? ''}
          onChange={e => set('country_code', (e.target.value || '').toUpperCase() || undefined)}
          placeholder="País (código, ej. ES)"
        />
      </div>

      {/* Fechas */}
      <div>
        <div className="panel-label" style={{ margin: '0 0 7px 0' }}>Fechas de extracción</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="cab-field" style={{ margin: 0 }}>
            <label>Desde</label>
            <input
              type="date"
              className="cab-input"
              value={(local.date_from ?? '').slice(0, 10)}
              onChange={e => set('date_from', e.target.value ? `${e.target.value}T00:00:00Z` : undefined)}
            />
          </div>
          <div className="cab-field" style={{ margin: 0 }}>
            <label>Hasta</label>
            <input
              type="date"
              className="cab-input"
              value={(local.date_to ?? '').slice(0, 10)}
              onChange={e => set('date_to', e.target.value ? `${e.target.value}T23:59:59Z` : undefined)}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={() => onApply(local)} className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }}>
          Aplicar
        </button>
        <button
          type="button"
          onClick={() => { setLocal({}); onClear() }}
          className="btn btn-ghost"
          title="Quitar todos los filtros"
          aria-label="Quitar todos los filtros"
        >
          <X size={13} />
        </button>
      </div>
    </aside>
  )
}

function FilterToggle({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean | undefined
  onChange: (v: boolean | undefined) => void
}) {
  const state = value === true ? 'yes' : value === false ? 'no' : 'any'
  return (
    <div className="ft-row">
      <span className="ft-label">{label}</span>
      <div className="ft-seg" role="group" aria-label={label}>
        <button type="button" onClick={() => onChange(undefined)} className={state === 'any' ? 'on' : ''}>Todo</button>
        <button type="button" onClick={() => onChange(true)} className={state === 'yes' ? 'on yes' : ''}>Sí</button>
        <button type="button" onClick={() => onChange(false)} className={state === 'no' ? 'on no' : ''}>No</button>
      </div>
    </div>
  )
}

/**
 * Resumen compacto de filtros activos en formato chip — útil para
 * disposiciones donde el panel de filtros está colapsado o oculto.
 */
export function FiltersInlineChips({
  filters,
  jobs,
  onClear,
  onRemove,
}: {
  filters: InboxFilters
  jobs: UserJobSummary[]
  onClear: () => void
  onRemove: (k: keyof InboxFilters) => void
}) {
  const chips: { key: keyof InboxFilters; label: string }[] = []
  if (filters.q) chips.push({ key: 'q', label: `Nombre: ${filters.q}` })
  if (filters.job_id) {
    const job = jobs.find(j => j.id === filters.job_id)
    chips.push({
      key: 'job_id',
      label: `Búsqueda: ${job ? jobLabel(job) : filters.job_id.slice(0, 8)}`,
    })
  }
  if (filters.has_email !== undefined)
    chips.push({
      key: 'has_email',
      label: filters.has_email ? 'Con email' : 'Sin email',
    })
  if (filters.has_phone !== undefined)
    chips.push({
      key: 'has_phone',
      label: filters.has_phone ? 'Con teléfono' : 'Sin teléfono',
    })
  if (filters.has_url !== undefined)
    chips.push({
      key: 'has_url',
      label: filters.has_url ? 'Con web' : 'Sin web',
    })
  if (filters.is_claimed !== undefined)
    chips.push({
      key: 'is_claimed',
      label: filters.is_claimed ? 'Reclamado' : 'No reclamado',
    })
  if (typeof filters.rating_min === 'number')
    chips.push({ key: 'rating_min', label: `Rating ≥ ${filters.rating_min}` })
  if (filters.country_code)
    chips.push({ key: 'country_code', label: `País: ${filters.country_code}` })
  if (filters.date_from)
    chips.push({
      key: 'date_from',
      label: `Desde: ${filters.date_from.slice(0, 10)}`,
    })
  if (filters.date_to)
    chips.push({
      key: 'date_to',
      label: `Hasta: ${filters.date_to.slice(0, 10)}`,
    })

  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map(c => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700"
        >
          {c.label}
          <button
            type="button"
            onClick={() => onRemove(c.key)}
            className="text-blue-500 hover:text-blue-700"
            aria-label="Quitar filtro"
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClear}
        className="text-[11px] font-medium text-gray-500 underline hover:text-gray-700"
      >
        Limpiar todos
      </button>
    </div>
  )
}

const SEM_GLYPH: Record<OppBand, string> = { high: '✓', mid: '!', low: '✕' }
const SEM_COLOR: Record<OppBand, string> = {
  high: 'var(--success)',
  mid: 'var(--warn)',
  low: 'var(--danger)',
}

/** Mini-anillo SVG del Opportunity Score (color por banda). */
function OppRing({ score, band }: { score: number; band: OppBand }) {
  const r = 10
  const c = 2 * Math.PI * r
  const off = c * (1 - score / 100)
  return (
    <svg className="opp-ring" viewBox="0 0 26 26" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx="13" cy="13" r={r} fill="none" stroke="var(--panel-2)" strokeWidth="3" />
      <circle
        cx="13" cy="13" r={r} fill="none" stroke={SEM_COLOR[band]} strokeWidth="3"
        strokeLinecap="round" strokeDasharray={c.toFixed(2)} strokeDashoffset={off.toFixed(2)}
      />
    </svg>
  )
}

interface HoverTip {
  x: number
  y: number
  score: number
  band: OppBand
  ideal: boolean
  breakdown: { label: string; points: number }[]
}

/**
 * Tabla de la bandeja, re-estilada al lenguaje de la cabina (hairlines +
 * números mono, firma V1). Añade la capa de inteligencia: columna OPORTUNIDAD
 * (anillo + semáforo + score con desglose al pasar el ratón) y SEÑALES, más
 * una acción para abrir la ficha de contacto. Las 24 columnas reales + el
 * ColumnPicker se conservan intactos.
 */
export function InboxTable({
  items,
  visibleCols,
  onOpenLead,
  sortByOpportunity = false,
  activeSort,
  onSort,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onToggleAll,
}: {
  items: InboxItem[]
  visibleCols: Set<ColumnId>
  onOpenLead?: (item: InboxItem) => void
  sortByOpportunity?: boolean
  /** Orden server-side activo + callback al clicar una cabecera ordenable (T-029). */
  activeSort?: InboxSort
  onSort?: (by: InboxOrderBy) => void
  /** Activa la columna de selección múltiple (para "Añadir al pipeline"). */
  selectable?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleAll?: () => void
}) {
  const visibleColumns = useMemo(
    () => COLUMN_CATALOG.filter(c => visibleCols.has(c.id)),
    [visibleCols],
  )
  const [tip, setTip] = useState<HoverTip | null>(null)
  const allSelected = selectable && items.length > 0 && items.every(i => selectedIds?.has(i.place_id))

  const scored = useMemo(() => {
    const rows = items.map(item => ({ item, sc: opportunityScore(item) }))
    // El orden server-side manda: si hay columna activa, NO reordenamos en cliente
    // (sería sobre la página visible). El sort por oportunidad solo aplica si no
    // hay orden de columna activo.
    if (sortByOpportunity && !activeSort?.by) rows.sort((a, b) => b.sc.score - a.sc.score)
    return rows
  }, [items, sortByOpportunity, activeSort?.by])

  return (
    <div className="table-wrap">
      <div className="table-scroll">
        <table className="cab-table">
          <thead>
            <tr>
              {selectable && (
                <th className="sel-col">
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todo"
                    checked={allSelected}
                    onChange={() => onToggleAll?.()}
                  />
                </th>
              )}
              <th>Oportunidad</th>
              <th>Señales</th>
              {visibleColumns.map(c => {
                const sortable = !!c.sortKey && !!onSort
                if (!sortable) return <th key={c.id} scope="col">{c.label}</th>
                const active = activeSort?.by === c.sortKey
                return (
                  <th
                    key={c.id}
                    scope="col"
                    aria-sort={active ? (activeSort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={'th-sort' + (active ? ' active' : '')}
                  >
                    <button
                      type="button"
                      className="th-sort-btn"
                      onClick={() => onSort?.(c.sortKey!)}
                      title={`Ordenar por ${c.label}`}
                    >
                      <span>{c.label}</span>
                      <span className="th-arrow" aria-hidden="true">
                        {active ? (activeSort?.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  </th>
                )
              })}
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {scored.map(({ item, sc }) => {
              const sigs = leadSignals(item).slice(0, 3)
              const isSel = selectedIds?.has(item.place_id) ?? false
              return (
                <tr key={item.place_id} className={isSel ? 'row-sel' : undefined} onClick={() => onOpenLead?.(item)}>
                  {selectable && (
                    <td className="sel-col" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label="Seleccionar lead"
                        checked={isSel}
                        onChange={() => onToggleSelect?.(item.place_id)}
                      />
                    </td>
                  )}
                  <td>
                    <span
                      className={`opp-cell ${sc.band}`}
                      onMouseEnter={e => {
                        const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
                        setTip({
                          x: r.left,
                          y: r.bottom + 6,
                          score: sc.score,
                          band: sc.band,
                          ideal: sc.ideal,
                          breakdown: sc.breakdown,
                        })
                      }}
                      onMouseLeave={() => setTip(null)}
                    >
                      <OppRing score={sc.score} band={sc.band} />
                      <b>{sc.score}</b>
                      <span className={`sem-dot ${sc.band}`} />
                    </span>
                  </td>
                  <td>
                    <span className="sigs">
                      {sigs.length > 0
                        ? sigs.map(s => (
                            <span className={`sig ${s.kind}`} key={s.label}>{s.label}</span>
                          ))
                        : <span style={{ color: 'var(--meta)' }}>—</span>}
                    </span>
                  </td>
                  {visibleColumns.map(c => (
                    <td
                      key={c.id}
                      className={c.id === 'name' ? 'name' : c.id === 'phone' || c.id === 'votes' || c.id === 'photos' || c.id === 'jobs_count' ? 'num' : undefined}
                    >
                      {c.render(item)}
                    </td>
                  ))}
                  <td>
                    <button
                      type="button"
                      className="row-act"
                      title="Contactar / ver ficha"
                      onClick={e => { e.stopPropagation(); onOpenLead?.(item) }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16v4l-6 6v6l-4-2v-4L4 8z" /></svg>
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {tip && (
        <div className="opp-tip" style={{ left: Math.min(tip.x, window.innerWidth - 260), top: tip.y }}>
          <div className={`tip-h ${tip.band}`}>
            <span className={`sem ${tip.band}`}>{SEM_GLYPH[tip.band]}</span>
            <b>{tip.score}</b>
            <span className="lab">{tip.band === 'high' ? 'Alto · prospecto ideal' : tip.band === 'mid' ? 'Medio' : 'Bajo'}</span>
          </div>
          {tip.breakdown.length > 0 ? (
            tip.breakdown.map(f => (
              <div className="tr" key={f.label}><span>{f.label}</span><span className="p">+{f.points}</span></div>
            ))
          ) : (
            <div className="tr"><span>Sin carencias detectadas</span></div>
          )}
          <div className="rule">
            <b>Fuerte pero carente:</b> mucha reputación + carencia detectada = a quién llamar primero.
          </div>
        </div>
      )}
    </div>
  )
}

export function ColumnPicker({
  visibleCols,
  onToggle,
  onReset,
  onShowAll,
  onClose,
  align = 'right',
}: {
  visibleCols: Set<ColumnId>
  onToggle: (id: ColumnId) => void
  onReset: () => void
  onShowAll: () => void
  onClose: () => void
  align?: 'left' | 'right'
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [onClose])

  return (
    <div
      ref={ref}
      className={`absolute top-full z-30 mt-1 w-72 rounded-md border border-gray-200 bg-white p-3 shadow-lg ${
        align === 'left' ? 'left-0' : 'right-0'
      }`}
    >
      <div className="mb-2 flex items-center justify-between text-[11px]">
        <span className="font-semibold text-gray-700">Mostrar columnas</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onShowAll}
            className="text-blue-600 hover:underline"
          >
            Todas
          </button>
          <span className="text-gray-300">·</span>
          <button
            type="button"
            onClick={onReset}
            className="text-gray-500 hover:underline"
          >
            Defaults
          </button>
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto pr-1">
        {COLUMN_CATALOG.map(c => {
          const checked = visibleCols.has(c.id)
          return (
            <label
              key={c.id}
              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs text-gray-700 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(c.id)}
                className="h-3.5 w-3.5"
              />
              <span>{c.label}</span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

export function ColumnPickerButton({
  visibleCols,
  toggleCol,
  resetCols,
  showAllCols,
  align = 'right',
}: {
  visibleCols: Set<ColumnId>
  toggleCol: (id: ColumnId) => void
  resetCols: () => void
  showAllCols: () => void
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="btn btn-ghost btn-sm"
      >
        <Columns size={12} />
        Columnas · {visibleCols.size}/{COLUMN_CATALOG.length}
      </button>
      {open && (
        <ColumnPicker
          visibleCols={visibleCols}
          onToggle={toggleCol}
          onReset={resetCols}
          onShowAll={showAllCols}
          onClose={() => setOpen(false)}
          align={align}
        />
      )}
    </div>
  )
}

export function ExportButtons({
  exportLoading,
  itemsCount,
  hasJobFilter,
  onExport,
}: {
  exportLoading: null | 'csv' | 'xlsx' | 'json'
  itemsCount: number
  hasJobFilter: boolean
  onExport: (format: 'csv' | 'xlsx' | 'json') => void
}) {
  return (
    <div className="exports">
      {(['csv', 'xlsx', 'json'] as const).map(fmt => (
        <button
          key={fmt}
          type="button"
          onClick={() => onExport(fmt)}
          disabled={exportLoading !== null || itemsCount === 0}
          title={
            hasJobFilter
              ? 'Descargar la búsqueda filtrada'
              : 'Descargar la página actual de la bandeja'
          }
        >
          {exportLoading === fmt ? '…' : fmt === 'csv' ? 'CSV' : fmt === 'xlsx' ? 'XLSX' : 'JSON'}
        </button>
      ))}
    </div>
  )
}

export function Paginator({
  offset,
  itemsLength,
  totalItems,
  pageSize,
  loading,
  onPrev,
  onNext,
}: {
  offset: number
  itemsLength: number
  totalItems: number
  pageSize: number
  loading: boolean
  onPrev: () => void
  onNext: () => void
}) {
  return (
    <div className="paginator">
      <span>
        Mostrando {offset + 1}–{offset + itemsLength} de{' '}
        {totalItems.toLocaleString('es-ES')} · página {Math.floor(offset / pageSize) + 1}
      </span>
      <div className="pg-btns">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onPrev} disabled={offset === 0 || loading}>← Anterior</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onNext} disabled={offset + itemsLength >= totalItems || loading}>Siguiente →</button>
      </div>
    </div>
  )
}

/**
 * Lista de búsquedas pasadas en formato vertical para mostrar en panel.
 * Permite al usuario filtrar la bandeja con un click sobre cualquier
 * búsqueda concreta (o volver a "todas mis búsquedas").
 */
export function JobsPastList({
  jobs,
  activeJobId,
  onSelect,
  onClearSelection,
  limit = 20,
}: {
  jobs: UserJobSummary[]
  activeJobId: string | undefined
  onSelect: (id: string) => void
  onClearSelection: () => void
  limit?: number
}) {
  const visible = jobs.slice(0, limit)
  return (
    <div>
      <button
        type="button"
        onClick={onClearSelection}
        className={`job-item${!activeJobId ? ' active' : ''}`}
      >
        <span className="j-name">Todas mis búsquedas</span>
        <span className="j-n">{jobs.length}</span>
      </button>
      {visible.map(j => {
        const isActive = activeJobId === j.id
        const st = jobStatusMeta(j.status)
        const zone = jobZoneLabel(j)
        // Cuándo terminó: hora de finalización si terminó; si no, el estado guía.
        const when =
          j.status === 'completed'
            ? `Terminó ${formatDateTimeShort(j.completed_at ?? j.created_at)}`
            : j.status === 'running' || j.status === 'pending'
              ? `Iniciada ${formatDateTimeShort(j.created_at)}`
              : formatDateTimeShort(j.created_at)
        return (
          <button
            key={j.id}
            type="button"
            onClick={() => onSelect(j.id)}
            className={`job-item rich${isActive ? ' active' : ''}`}
          >
            <span className="ji-top">
              <span className="j-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {jobCategoryLabel(j)}
              </span>
              <span className="j-n">{jobLeads(j)} <small>leads</small></span>
            </span>
            <span className="ji-bot">
              <span className={`status ${st.cls}`}><span className="d" />{st.label}</span>
              <span className="j-meta" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {zone ? `${zone} · ` : ''}{when}
              </span>
            </span>
          </button>
        )
      })}
      {jobs.length > limit && (
        <p style={{ fontSize: 10, color: 'var(--meta)', padding: '6px 2px 0' }}>
          +{jobs.length - limit} más en el selector
        </p>
      )}
    </div>
  )
}

/** Agrupa los jobs (ya ordenados por fecha desc) por mes natural, conservando
 *  el orden. Devuelve [{ key, label, jobs }] con el mes capitalizado en ES. */
function groupJobsByMonth(
  jobs: UserJobSummary[],
): { key: string; label: string; jobs: UserJobSummary[] }[] {
  const groups: { key: string; label: string; jobs: UserJobSummary[] }[] = []
  const byKey = new Map<string, { key: string; label: string; jobs: UserJobSummary[] }>()
  for (const j of jobs) {
    const d = new Date(j.created_at)
    const valid = !Number.isNaN(d.getTime())
    const key = valid ? `${d.getFullYear()}-${d.getMonth()}` : 'sin-fecha'
    let g = byKey.get(key)
    if (!g) {
      const raw = valid
        ? d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
        : 'Sin fecha'
      const label = raw.charAt(0).toUpperCase() + raw.slice(1)
      g = { key, label, jobs: [] }
      byKey.set(key, g)
      groups.push(g)
    }
    g.jobs.push(j)
  }
  return groups
}

/**
 * Historial completo de búsquedas (pestaña "Historial" de Búsqueda) — lenguaje
 * CABINA: una franja de mini-stats de resumen + grupos por mes con cabecera
 * sticky + filas-TARJETA limpias (icono de categoría, categoría ES como título +
 * zona como subtítulo, estado en pill, nº de leads con peso visual, fecha/hora
 * discreta y acciones discretas "Ver leads" ghost + "Repetir" icono). Más
 * recientes arriba. Claro/oscuro. Responsive: tarjetas apiladas en móvil.
 */
export function JobsHistory({
  jobs,
  total,
  loading,
  loaded,
  capped,
  error,
  activeJobId,
  onViewLeads,
  onResume,
}: {
  jobs: UserJobSummary[]
  total: number
  loading: boolean
  loaded: boolean
  capped: boolean
  error: boolean
  activeJobId?: string
  onViewLeads: (jobId: string) => void
  onResume: (job: UserJobSummary) => void
}) {
  const sorted = useMemo(
    () =>
      [...jobs].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [jobs],
  )
  const groups = useMemo(() => groupJobsByMonth(sorted), [sorted])

  const totalLeads = useMemo(
    () => sorted.reduce((s, j) => s + jobLeads(j), 0),
    [sorted],
  )
  const completedCount = useMemo(
    () => sorted.filter(j => j.status === 'completed').length,
    [sorted],
  )

  if (loading && !loaded) {
    return (
      <div className="history-state">
        <Loader2 size={18} className="animate-spin" />
        Cargando tu historial de búsquedas…
      </div>
    )
  }

  if (error && jobs.length === 0) {
    return (
      <div className="history-state">
        No se pudo cargar el historial. Recarga la página para reintentar.
      </div>
    )
  }

  if (sorted.length === 0) {
    return (
      <div className="history-state">
        Todavía no tienes búsquedas. Lanza tu primera extracción desde la pestaña
        de leads.
      </div>
    )
  }

  return (
    <div className="history">
      {/* Resumen — mini-stats al estilo de las tarjetas de la Home. */}
      <div className="history-stats">
        <div className="hstat accent">
          <span className="hv">{total.toLocaleString('es-ES')}</span>
          <span className="hl">{total === 1 ? 'búsqueda en total' : 'búsquedas en total'}</span>
        </div>
        <div className="hstat">
          <span className="hv">{totalLeads.toLocaleString('es-ES')}</span>
          <span className="hl">leads extraídos</span>
        </div>
        <div className="hstat">
          <span className="hv">{completedCount.toLocaleString('es-ES')}</span>
          <span className="hl">{completedCount === 1 ? 'búsqueda terminada' : 'búsquedas terminadas'}</span>
        </div>
      </div>
      {capped && (
        <span className="history-cap">
          Mostrando las {sorted.length.toLocaleString('es-ES')} más recientes
        </span>
      )}

      {groups.map(g => (
        <section className="hist-group" key={g.key}>
          <div className="hist-group-head">
            <span className="hg-month">{g.label}</span>
            <span className="hg-count">
              {g.jobs.length} {g.jobs.length === 1 ? 'búsqueda' : 'búsquedas'}
            </span>
            <span className="hg-line" />
          </div>

          <div className="hist-list">
            {g.jobs.map(j => {
              const st = jobStatusMeta(j.status)
              const zone = jobZoneLabel(j)
              const cats = j.categories ?? j.query?.categories ?? []
              const canResume = cats.length > 0
              const isActive = activeJobId === j.id
              return (
                <div className={`hist-card${isActive ? ' active' : ''}`} key={j.id}>
                  <span className="hc-ico"><SearchIcon size={18} /></span>

                  <span className="hc-what">
                    <span className="hc-cat">{jobCategoryLabel(j)}</span>
                    {zone && (
                      <span className="hc-zone">
                        <MapPin size={12} />
                        <span>{zone}</span>
                      </span>
                    )}
                  </span>

                  <span className="hc-meta">
                    <span className={`status ${st.cls}`}>
                      <span className="d" />
                      {st.label}
                    </span>
                    <span className="hc-leads">
                      <b>{jobLeads(j).toLocaleString('es-ES')}</b>
                      <small>leads</small>
                    </span>
                    <span className="hc-when">
                      {formatDateTimeShort(
                        j.status === 'completed'
                          ? j.completed_at ?? j.created_at
                          : j.created_at,
                      )}
                    </span>
                  </span>

                  <span className="hc-act">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm h-view"
                      onClick={() => onViewLeads(j.id)}
                      title="Filtrar la tabla por esta búsqueda"
                    >
                      Ver leads
                      <ArrowRight size={13} />
                    </button>
                    <button
                      type="button"
                      className="h-iconbtn"
                      onClick={() => onResume(j)}
                      disabled={!canResume}
                      aria-label="Repetir búsqueda"
                      title={
                        canResume
                          ? 'Repetir: rellena las categorías de esta búsqueda (elige zona y estima de nuevo)'
                          : 'Sin categorías guardadas'
                      }
                    >
                      <RefreshCw size={14} />
                    </button>
                  </span>
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}

/**
 * KPIs resumidos del job activo (cuando hay polling) o del último job
 * completado si no hay activo. Útil para disposiciones tipo dashboard.
 */
export function JobKpis({
  activeJob,
  jobs,
}: {
  activeJob: SearchJob | null
  jobs: UserJobSummary[]
}) {
  if (activeJob) {
    const pct = Math.round(activeJob.progress_percent ?? 0)
    return (
      <div>
        <div className="kpi-grid">
          <div className="kpi lead">
            <div className="k-label">{activeJob.status === 'running' ? 'Extrayendo' : 'En cola'}</div>
            <div className="k-val">{activeJob.result_listings_count.toLocaleString('es-ES')}</div>
          </div>
          <div className="kpi"><div className="k-label">Progreso</div><div className="k-val">{pct}%</div></div>
          <div className="kpi"><div className="k-label">Estimado</div><div className="k-val">{activeJob.total_count_estimate ? `~${activeJob.total_count_estimate}` : '—'}</div></div>
        </div>
        <div className="track" style={{ marginTop: 11 }}><i style={{ width: `${pct}%` }} /></div>
      </div>
    )
  }
  const last = jobs[0]
  if (!last) {
    return <p className="empty-hint" style={{ textAlign: 'center' }}>Aún no hay búsquedas. Lanza la primera.</p>
  }
  return (
    <div className="kpi-grid">
      <div className="kpi lead"><div className="k-label">Última</div><div className="k-val" style={{ fontSize: 13 }}>{formatDateShort(last.created_at)}</div></div>
      <div className="kpi"><div className="k-label">Leads</div><div className="k-val">{(last.result_listings_count ?? 0).toLocaleString('es-ES')}</div></div>
      <div className="kpi"><div className="k-label">Estado</div><div className="k-val" style={{ fontSize: 13, color: last.status === 'completed' ? 'var(--success)' : 'var(--muted)' }}>{last.status === 'completed' ? 'OK' : last.status}</div></div>
    </div>
  )
}

export function InboxEmptyState({
  hasFilters,
  loading,
}: {
  hasFilters: boolean
  loading: boolean
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
        <Loader2 size={18} className="mx-auto mb-2 animate-spin" />
        Cargando bandeja…
      </div>
    )
  }
  return (
    <div className="rounded-lg border border-dashed border-gray-200 bg-white p-12 text-center">
      <p className="text-sm font-medium text-gray-700">
        Tu bandeja está vacía{hasFilters ? ' con estos filtros' : ''}.
      </p>
      <p className="mt-1 text-xs text-gray-500">
        {hasFilters
          ? 'Prueba a quitar algún filtro o lanza una nueva extracción.'
          : 'Lanza una nueva extracción desde el panel de nueva búsqueda.'}
      </p>
    </div>
  )
}

export function InboxErrorBanner({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <div
      role="alert"
      className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
    >
      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
      <span>{message}</span>
    </div>
  )
}

/**
 * Modal de confirmación DEC-024 capa 3 reutilizable en cualquier mockup.
 * Se renderiza siempre — solo es visible cuando state.confirmModalOpen.
 */
export function ConfirmModal({ state }: { state: UseSearchAndInbox }) {
  if (!state.estimate) return null
  return (
    <ConfirmThresholdModal
      open={state.confirmModalOpen}
      leadsToConsume={state.estimate.leads_to_consume}
      confirmThreshold={state.estimate.confirm_threshold}
      leadsRemainingAfter={state.estimate.leads_remaining_after}
      planCode={state.estimate.plan_code}
      loading={state.searchLoading}
      onCancel={state.closeConfirmModal}
      onConfirm={state.confirmExtract}
    />
  )
}

export function FiltersIndicator({ count }: { count: number }) {
  if (count === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Filter size={12} />
        Sin filtros
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
      <Filter size={12} />
      {count} filtro{count === 1 ? '' : 's'} activo{count === 1 ? '' : 's'}
    </span>
  )
}

// ============================================================================
// Helpers
// ============================================================================

export function shortUrl(url: string): string {
  try {
    const u = new URL(url)
    return u.hostname + (u.pathname && u.pathname !== '/' ? u.pathname : '')
  } catch {
    return url
  }
}

export function statusLabel(s: string | null): string {
  if (!s) return '—'
  if (s === 'open') return 'Abierto'
  if (s === 'close') return 'Cerrado hoy'
  if (s === 'closed_forever') return 'Cerrado'
  return s
}

export function hasActiveFilters(f: InboxFilters): boolean {
  return Object.values(f).some(v => v !== undefined && v !== '')
}

export function countActiveFilters(f: InboxFilters): number {
  return Object.values(f).filter(v => v !== undefined && v !== '').length
}

/** Estado del job → etiqueta clara en español + clase de color (cabina). */
export function jobStatusMeta(status: string): { label: string; cls: 'ok' | 'run' | 'fail' } {
  switch (status) {
    case 'completed': return { label: 'Terminada', cls: 'ok' }
    case 'running': return { label: 'En curso', cls: 'run' }
    case 'pending': return { label: 'En cola', cls: 'run' }
    case 'failed': return { label: 'Fallida', cls: 'fail' }
    case 'cancelled':
    case 'canceled': return { label: 'Detenida', cls: 'fail' }
    default: return { label: status, cls: 'run' }
  }
}

/**
 * Etiqueta de la ZONA buscada de un job. Shape real T-022: el backend expone
 * `zone_label` en la RAÍZ del item. Orden de resolución: `item.zone_label`
 * (raíz) → `query.zone_query.name` (compat/demo) → coordenadas legibles
 * (free_circle) → "Coordenadas" → "Zona seleccionada".
 */
export function jobZoneLabel(j: UserJobSummary): string | null {
  if (typeof j.zone_label === 'string' && j.zone_label.trim()) return j.zone_label
  const zq = (j.query ?? {}).zone_query as Record<string, unknown> | undefined
  if (zq && typeof zq === 'object') {
    if (typeof zq.name === 'string' && zq.name.trim()) return zq.name
    const lat = Number(zq.center_lat)
    const lng = Number(zq.center_lng)
    if (zq.kind === 'free_circle' && Number.isFinite(lat) && Number.isFinite(lng)) {
      const r = Number.isFinite(Number(zq.radius_km)) ? ` · ${Number(zq.radius_km)} km` : ''
      return `Coordenadas (${lat.toFixed(2)}, ${lng.toFixed(2)})${r}`
    }
  }
  // Job por coordenadas sin etiqueta, o solo zone_id sin nombre.
  if ((j.query ?? {}).zone_query) return 'Coordenadas'
  if ((j.query ?? {}).zone_id != null) return 'Zona seleccionada'
  return null
}

/** Categorías del job (slugs en la RAÍZ del item, T-022; compat con query.categories)
 *  traducidas a español (categoryLabel) con "+N" si hay más. */
export function jobCategoryLabel(j: UserJobSummary): string {
  const cats = j.categories ?? j.query?.categories ?? []
  if (cats.length === 0) return 'Sin categorías'
  const shown = cats.slice(0, 2).map(c => categoryLabel(c)).join(', ')
  return cats.length > 2 ? `${shown} +${cats.length - 2}` : shown
}

export function jobLeads(j: UserJobSummary): number {
  return j.result_listings_count ?? 0
}

/** Fecha + hora corta (es-ES) para "cuándo terminó". */
export function formatDateTimeShort(iso: string | null): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    const date = d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
    const time = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    return `${date} · ${time}`
  } catch {
    return iso
  }
}

/** Etiqueta de una línea para el `<option>` del selector de filtros: en
 *  español y con info completa (fecha · categoría ES · zona · N leads · estado). */
export function jobLabel(j: UserJobSummary): string {
  const zone = jobZoneLabel(j)
  return [
    formatDateShort(j.created_at),
    jobCategoryLabel(j),
    zone,
    `${jobLeads(j)} leads`,
    jobStatusMeta(j.status).label,
  ].filter(Boolean).join(' · ')
}

export function formatDateShort(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  } catch {
    return iso
  }
}

function triggerDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

function inboxToCsv(items: InboxItem[]): string {
  const cols = [
    'place_id',
    'title',
    'category_primary',
    'address_full',
    'city',
    'country_code',
    'phone',
    'email_primary',
    'url',
    'rating_value',
    'rating_votes_count',
    'is_claimed',
    'jobs_count',
    'first_extracted_at',
  ] as const
  const escape = (v: unknown): string => {
    if (v === null || v === undefined) return ''
    const s = String(v)
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const header = cols.join(',')
  const rows = items.map(i =>
    [
      i.place_id,
      i.title,
      i.category_primary,
      i.address_full,
      i.address_components?.city ?? '',
      i.address_components?.country_code ?? '',
      i.phone,
      i.email_primary,
      i.url,
      i.rating_value,
      i.rating_votes_count,
      i.is_claimed,
      i.jobs_count,
      i.first_extracted_at,
    ]
      .map(escape)
      .join(','),
  )
  return [header, ...rows].join('\n')
}
